import React, { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import Layout from '../components/Layout'
import { api } from '../lib/api'
import { Badge } from './Admin'
import { Button, Card, ErrorMessage, Field, Spinner, Stat } from '../components/ui'

function entitlementSummary(entitlement) {
  if (!entitlement) return '—'

  switch (entitlement.source) {
    case 'comp':
      return 'Comped — no expiry'
    case 'subscription':
      return `Subscribed until ${new Date(entitlement.expires_at).toLocaleDateString()}`
    default:
      return `Free — ${entitlement.free_used} of ${entitlement.free_limit} used`
  }
}

export default function AdminUser() {
  const { userId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // One reason box for every action on the page. The server records it against
  // whichever one is used, and an empty reason is allowed but discouraged —
  // this field is the only thing that answers "why did my access change".
  const [reason, setReason] = useState('')
  const [months, setMonths] = useState('12')
  const [limit, setLimit] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')

  const user = useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: () => api.adminUser(userId),
    retry: false,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin'] })

  const grantFlag = useMutation({
    mutationFn: () => api.adminGrantComp(userId, { mode: 'flag', reason }),
    onSuccess: refresh,
  })
  const grantMonths = useMutation({
    mutationFn: () => api.adminGrantComp(userId, { mode: 'months', months: Number(months), reason }),
    onSuccess: refresh,
  })
  const revoke = useMutation({
    mutationFn: () => api.adminRevokeComp(userId, reason),
    onSuccess: refresh,
  })
  const setFreeLimit = useMutation({
    mutationFn: () => api.adminSetFreeLimit(userId, limit === '' ? null : Number(limit), reason),
    onSuccess: refresh,
  })
  const sendReset = useMutation({
    mutationFn: () => api.adminSendPasswordReset(userId, reason),
    onSuccess: refresh,
  })
  const destroy = useMutation({
    mutationFn: () => api.adminDeleteUser(userId, confirmEmail, reason),
    onSuccess: () => {
      refresh()
      navigate('/admin')
    },
  })

  if (user.isLoading) {
    return (
      <Layout>
        <Spinner />
      </Layout>
    )
  }

  if (user.error) {
    return (
      <Layout>
        <Card title="Not available">
          <p className="text-sm text-slate-400">
            That user could not be loaded.{' '}
            <Link to="/admin" className="underline">
              Back to users
            </Link>
            .
          </p>
        </Card>
      </Layout>
    )
  }

  const record = user.data.user
  const busy = [ grantFlag, grantMonths, revoke, setFreeLimit, sendReset, destroy ]
  const error = busy.find((mutation) => mutation.error)?.error

  return (
    <Layout wide>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <Link to="/admin" className="text-xs text-slate-400 hover:text-slate-200">
              ← Users
            </Link>
            <h1 className="mt-1 text-lg font-semibold text-slate-100">{record.email_address}</h1>
          </div>
          <div className="flex items-center gap-2">
            {record.admin && <Badge tone="amber">admin</Badge>}
            {record.comped && <Badge tone="emerald">comped</Badge>}
          </div>
        </div>

        <ErrorMessage error={error} />

        <Card title="Entitlement">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Access" value={entitlementSummary(record.entitlement)} />
            <Stat label="Free used" value={`${record.entitlement.free_used}/${record.entitlement.free_limit}`} />
            <Stat label="Leagues" value={record.leagues_count} />
            <Stat label="Evaluated" value={record.evaluated_trades_count} />
          </div>
        </Card>

        <Card
          title="Reason"
          subtitle="Recorded against whichever action you take below."
        >
          <Field
            label="Why"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="support request #12 — evaluation failed after payment"
          />
        </Card>

        <Card title="Comp" subtitle="A flag never expires; months are for putting someone back where they were.">
          <div className="flex flex-wrap items-end gap-2">
            <Button onClick={() => grantFlag.mutate()} disabled={record.comped}>
              Comp indefinitely
            </Button>

            <div className="w-24">
              <Field
                label="Months"
                type="number"
                min="1"
                value={months}
                onChange={(event) => setMonths(event.target.value)}
              />
            </div>
            <Button variant="secondary" onClick={() => grantMonths.mutate()}>
              Grant months
            </Button>

            <Button variant="secondary" onClick={() => revoke.mutate()}>
              Revoke comps
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Revoking clears the flag and ends comp subscriptions. Anything they actually paid for is
            left alone.
          </p>
        </Card>

        <Card title="Free allowance" subtitle="Overrides the default and any running experiment. Blank to clear.">
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-32">
              <Field
                label="Limit"
                type="number"
                min="0"
                value={limit}
                onChange={(event) => setLimit(event.target.value)}
                placeholder={String(record.entitlement.free_limit)}
              />
            </div>
            <Button variant="secondary" onClick={() => setFreeLimit.mutate()}>
              Set allowance
            </Button>
          </div>
          {record.free_evaluation_limit_override !== null && (
            <p className="mt-2 text-xs text-slate-500">
              Currently overridden to {record.free_evaluation_limit_override}. This user is excluded
              from allowance experiments.
            </p>
          )}
        </Card>

        <Card title="Password">
          <Button variant="secondary" onClick={() => sendReset.mutate()}>
            Send reset email
          </Button>
          <p className="mt-2 text-xs text-slate-500">
            Sends the same email they would request themselves. It does not set a password, and you
            will not see one — their current password keeps working until they use the link.
          </p>
          {sendReset.isSuccess && (
            <p className="mt-2 text-xs text-emerald-400">Sent to {record.email_address}.</p>
          )}
        </Card>

        <Card title="Delete account" subtitle="Irreversible. Type the email address to confirm.">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-64 flex-1">
              <Field
                label="Confirm email"
                value={confirmEmail}
                onChange={(event) => setConfirmEmail(event.target.value)}
                placeholder={record.email_address}
                autoComplete="off"
              />
            </div>
            <Button
              variant="secondary"
              className="border-rose-800 text-rose-300 hover:bg-rose-950"
              onClick={() => destroy.mutate()}
              disabled={confirmEmail.trim().toLowerCase() !== record.email_address}
            >
              Delete
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Refused while a Stripe or Apple subscription is still granting — deleting the account
            does not cancel their billing, and they would keep paying for access they no longer have.
          </p>
        </Card>

        <Card title="Subscriptions">
          {record.subscriptions.length === 0 ? (
            <p className="text-sm text-slate-400">None.</p>
          ) : (
            <ul className="divide-y divide-slate-800 text-sm">
              {record.subscriptions.map((subscription) => (
                <li key={subscription.id} className="flex flex-wrap justify-between gap-2 py-2">
                  <span className="text-slate-200">
                    {subscription.provider} · {subscription.status}
                    {subscription.granting && <span className="ml-2 text-emerald-400">granting</span>}
                  </span>
                  <span className="text-slate-500">
                    until {new Date(subscription.current_period_end).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="History">
          {record.recent_actions.length === 0 ? (
            <p className="text-sm text-slate-400">Nothing recorded.</p>
          ) : (
            <ul className="divide-y divide-slate-800 text-sm">
              {record.recent_actions.map((action, index) => (
                <li key={index} className="py-2">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="text-slate-200">{action.kind}</span>
                    <span className="text-xs text-slate-500">
                      {new Date(action.created_at).toLocaleString()} · {action.admin_email}
                    </span>
                  </div>
                  {action.reason && (
                    <p className="mt-0.5 text-xs text-slate-400">{action.reason}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </Layout>
  )
}
